import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import { useSpring, animated } from "@react-spring/three";
import { Text } from "@react-three/drei";

function ServiceCard3D({ service, dark, index }) {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);
  
  // Remove spring animation, use static values
  const scale = 1;
  const rotation = [0, 0, 0];
  const position = [0, 0, 0];

  useFrame(({ clock }) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = 0;
      meshRef.current.position.y = 0;
    }
  });

  const colors = {
    primary: dark ? "#a5b4fc" : "#1b1c4e",
    secondary: dark ? "#818cf8" : "#23244d",
    accent: dark ? "#6366f1" : "#3730a3",
    glow: dark ? "#a5b4fc" : "#6366f1"
  };

  const handleClick = () => {
    setClicked(!clicked);
    setTimeout(() => setClicked(false), 1000);
  };

  return (
    <animated.group
      ref={meshRef}
      scale={scale}
      rotation={rotation}
      position={position}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
      onClick={handleClick}
    >
      {/* Main card body */}
      <mesh>
        <boxGeometry args={[2.2, 3.5, 0.2]} />
        <meshStandardMaterial 
          color={colors.primary}
          roughness={0.3}
          metalness={0.7}
          transparent
          opacity={0.9}
        />
      </mesh>
      
      {/* Icon sphere */}
      <mesh position={[0, 1, 0.15]}>
        <sphereGeometry args={[0.4, 16, 16]} />
        <meshStandardMaterial 
          color={colors.accent}
          roughness={0.2}
          metalness={0.8}
        />
      </mesh>
      
      {/* 3D Text for Service Title */}
      <Text
        position={[0, 0.7, 0.25]}
        fontSize={0.32}
        color={dark ? '#e0e7ff' : '#23244d'}
        anchorX="center"
        anchorY="middle"
        outlineColor={dark ? '#6366f1' : '#818cf8'}
        outlineWidth={0.01}
        maxWidth={1.8}
      >
        {service.title}
      </Text>
      
      {/* Title area */}
      <mesh position={[0, 0.3, 0.15]}>
        <boxGeometry args={[2, 0.8, 0.05]} />
        <meshStandardMaterial 
          color={colors.secondary}
          roughness={0.5}
          metalness={0.3}
        />
      </mesh>
      
      {/* Description area */}
      <mesh position={[0, -0.3, 0.15]}>
        <boxGeometry args={[2, 0.8, 0.05]} />
        <meshStandardMaterial 
          color={colors.secondary}
          roughness={0.5}
          metalness={0.3}
        />
      </mesh>
      
      {/* Features area */}
      <mesh position={[0, -1, 0.15]}>
        <boxGeometry args={[2, 1, 0.05]} />
        <meshStandardMaterial 
          color={colors.secondary}
          roughness={0.5}
          metalness={0.3}
        />
      </mesh>
      
      {/* Tech stack indicators */}
      {service.tech && service.tech.slice(0, 3).map((tech, techIndex) => (
        <mesh key={tech} position={[-0.8 + techIndex * 0.8, -1.5, 0.15]}>
          <cylinderGeometry args={[0.1, 0.1, 0.1, 8]} />
          <meshStandardMaterial 
            color={colors.accent}
            roughness={0.3}
            metalness={0.6}
          />
        </mesh>
      ))}
      
      {/* Enhanced glow effect when hovered */}
      {hovered && (
        <mesh position={[0, 0, 0.4]}>
          <sphereGeometry args={[2, 16, 16]} />
          <meshBasicMaterial 
            color={colors.glow}
            transparent
            opacity={0.2}
          />
        </mesh>
      )}
      
      {/* Click effect */}
      {clicked && (
        <mesh position={[0, 0, 0.5]}>
          <sphereGeometry args={[2.5, 16, 16]} />
          <meshBasicMaterial 
            color={colors.glow}
            transparent
            opacity={0.4}
          />
        </mesh>
      )}
    </animated.group>
  );
}

function ServiceCardsContainer({ services, dark }) {
  return (
    <div className="relative w-full overflow-x-auto">
      <div className="flex flex-row gap-8 justify-center min-w-[900px] pb-8">
        {services.map((service, index) => (
          <div key={service.title} className="relative" style={{ width: 220, height: 350 }}>
            {/* 3D Card Canvas */}
            <Canvas camera={{ position: [0, 0, 8], fov: 50 }} style={{ width: 220, height: 350 }}>
              <ambientLight intensity={0.6} />
              <directionalLight position={[5, 5, 5]} intensity={1} />
              <pointLight position={[-5, -5, 5]} intensity={0.5} color={dark ? "#a5b4fc" : "#1b1c4e"} />
              <ServiceCard3D service={service} dark={dark} index={index} />
            </Canvas>
            {/* HTML Overlay for clarity */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-3xl mb-2 pointer-events-none select-none">{service.icon}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ServiceCards3D({ dark }) {
  const [selectedService, setSelectedService] = useState(null);
  
  const services = [
    {
      title: "Website & Web App Development",
      icon: "🌐",
      description: "Responsive business websites, custom CMS, booking systems, blogs, e-commerce, hosting, optimization, and more.",
      color: "from-blue-500 to-cyan-500",
      features: ["Custom CMS", "E-commerce", "Booking Systems", "Optimization"],
      tech: ["React", "Node.js", "MongoDB", "AWS"]
    },
    {
      title: "Mobile App Development",
      icon: "📱",
      description: "Android/iOS native and hybrid apps, real-time tools, Play Store publishing, payment/location/QR integrations.",
      color: "from-green-500 to-emerald-500",
      features: ["Android/iOS", "Hybrid Apps", "Real-time Tools", "QR Integrations"],
      tech: ["React Native", "Flutter", "Swift", "Kotlin"]
    },
    {
      title: "UI/UX & Branding Design",
      icon: "🎨",
      description: "Logo, banners, business cards, videos, flyers, app UI kits, landing pages, and more.",
      color: "from-purple-500 to-pink-500",
      features: ["Logo Design", "UI Kits", "Landing Pages", "Branding"],
      tech: ["Figma", "Adobe XD", "Sketch", "InVision"]
    },
    {
      title: "Digital Marketing",
      icon: "🚀",
      description: "SEO, SEM, social media, email, content, analytics, and campaign optimization.",
      color: "from-orange-500 to-red-500",
      features: ["SEO", "SEM", "Social Media", "Analytics"],
      tech: ["Google Analytics", "SEMrush", "Hootsuite", "Mailchimp"]
    },
    {
      title: "Business Strategy & Consulting",
      icon: "🧠",
      description: "Digital transformation, local growth, e-commerce, business listing, Google My Business, and more.",
      color: "from-indigo-500 to-blue-500",
      features: ["Digital Transformation", "E-commerce", "Business Listing", "Consulting"],
      tech: ["Google My Business", "Shopify", "Stripe", "Zapier"]
    },
    {
      title: "Notification & Integration Services",
      icon: "🔔",
      description: "WhatsApp/SMS/email APIs, CRM setup, automation, lead management, OTP verification.",
      color: "from-yellow-500 to-orange-500",
      features: ["WhatsApp API", "CRM Setup", "Automation", "OTP Verification"],
      tech: ["Twilio", "HubSpot", "Mailgun", "Firebase"]
    },
    {
      title: "Tools & Utility Development",
      icon: "🧰",
      description: "Custom search engines, document tools, calculators, e-learning, RSS, and more.",
      color: "from-teal-500 to-green-500",
      features: ["Search Engines", "Calculators", "E-learning", "Document Tools"],
      tech: ["Python", "Node.js", "React", "AWS"]
    },
    {
      title: "Startup & Business Support",
      icon: "📦",
      description: "Branding packages, registration, MVP/SaaS launch, free tools, and startup bundles.",
      color: "from-rose-500 to-pink-500",
      features: ["Branding", "MVP Launch", "SaaS", "Startup Bundles"],
      tech: ["Stripe", "WordPress", "Shopify", "Zapier"]
    }
  ];

  return (
    <section id="services" className="relative py-20 px-4 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-indigo-900/20 dark:via-[#1b1c4e]/30 dark:to-purple-900/20" />
      
      <div className="relative max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#1b1c4e] dark:text-white mb-4">
            Our Services
          </h2>
          <p className="text-lg text-[#23244d] dark:text-white/80 max-w-2xl mx-auto">
            Comprehensive digital solutions
          </p>
        </motion.div>

        {/* <ServiceCardsContainer services={services} dark={dark} /> */}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-8"
        >
          {/* <p className="text-sm text-[#23244d] dark:text-white/60 mb-4">
            Hover over the cards to see them in action • Click for detailed information
          </p> */}
          
          {/* Service Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className={`p-6 rounded-xl backdrop-blur-md border-2 transition-all duration-300 cursor-pointer ${
                  dark 
                    ? 'bg-white/10 border-indigo-400/30 hover:border-indigo-400/60' 
                    : 'bg-white/80 border-indigo-200/50 hover:border-indigo-300/80'
                }`}
                onClick={() => setSelectedService(service)}
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-[#1b1c4e] dark:text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-[#23244d] dark:text-white/80 mb-4">
                  {service.description}
                </p>
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-[#1b1c4e] dark:text-white">
                    Key Features:
                  </h4>
                  <ul className="text-xs text-[#23244d] dark:text-white/70 space-y-1">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mr-2"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-4 pt-4 border-t border-indigo-200/30 dark:border-indigo-400/30">
                  <h4 className="font-semibold text-sm text-[#1b1c4e] dark:text-white mb-2">
                    Technologies:
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {service.tech.slice(0, 4).map((tech, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-1 text-xs rounded-full ${
                          dark 
                            ? 'bg-indigo-600/30 text-indigo-200' 
                            : 'bg-indigo-100 text-indigo-700'
                        }`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* Service Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedService(null)}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`relative max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl p-8 ${
                dark 
                  ? 'bg-[#1b1c4e] text-white border-2 border-indigo-400/30' 
                  : 'bg-white text-[#1b1c4e] border-2 border-indigo-200/50'
              } shadow-2xl`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedService(null)}
                className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
                  dark 
                    ? 'hover:bg-indigo-600/30 text-white' 
                    : 'hover:bg-indigo-100 text-[#1b1c4e]'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{selectedService.icon}</div>
                <h2 className="text-3xl font-bold mb-2">{selectedService.title}</h2>
                <p className="text-lg opacity-80">{selectedService.description}</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Key Features</h3>
                  <ul className="space-y-3">
                    {selectedService.features.map((feature, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-start space-x-3"
                      >
                        <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          dark ? 'bg-indigo-400' : 'bg-indigo-600'
                        }`} />
                        <span className="opacity-90">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-4">Technologies</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedService.tech.map((tech, idx) => (
                      <motion.span
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.05 }}
                        className={`px-3 py-2 rounded-full text-sm font-medium ${
                          dark 
                            ? 'bg-indigo-600/30 text-indigo-200 border border-indigo-400/30' 
                            : 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                        }`}
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-indigo-200/30 dark:border-indigo-400/30">
                <div className="text-center">
                  <button
                    className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      dark 
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                    onClick={() => {
                      setSelectedService(null);
                      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Get Started with {selectedService.title}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
} 